import { inject } from '@angular/core';
import { CanActivateFn, CanDeactivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from 'shared-lib';
import { environment } from 'projects/self-care/src/environments/environment';
import { SignUpService } from './sign-up.service';

export const signUpDisabledGuard: CanActivateFn = () => {
  if (environment.envName === 'isp360') {
    return inject(Router).createUrlTree(['/auth/login']);
  }
  return true;
};

export const signUpOtpGuard: CanActivateFn = () => {
  const signUpService = inject(SignUpService);
  const router = inject(Router);

  if (signUpService.hasOtpState()) {
    return true;
  }

  signUpService.reset();
  return router.createUrlTree(['/auth/sign-up']);
};

export const signUpPasswordGuard: CanActivateFn = () => {
  const signUpService = inject(SignUpService);
  const router = inject(Router);

  if (signUpService.hasPasswordState()) {
    return true;
  }

  signUpService.reset();
  return router.createUrlTree(['/auth/sign-up']);
};

// Guards against the confirmation dialog being triggered a second time for the same logical
// "discard progress" action. Confirming the dialog calls signUpService.reset(), which makes
// signUpOtpGuard's hasOtpState() check fail and redirect to the sign-up start. That redirect
// cancels the in-flight navigation and starts a new one still leaving the (still-active)
// password route, so the Router re-invokes this canDeactivate guard immediately afterwards.
// discardConfirmed lets that immediate follow-up invocation through without prompting again.
let pendingDiscardConfirmation: Promise<boolean> | null = null;
let discardConfirmed = false;

export const signUpPasswordCanDeactivateGuard: CanDeactivateFn<unknown> = () => {
  const signUpService = inject(SignUpService);
  const router = inject(Router);
  const utils = inject(UtilsService);
  const translate = inject(TranslateService);
  const navigation = router.getCurrentNavigation();

  if (navigation?.extras?.state?.['signUpComplete']) {
    signUpService.reset();
    return true;
  }

  if (discardConfirmed) {
    discardConfirmed = false;
    return true;
  }

  if (pendingDiscardConfirmation) {
    return pendingDiscardConfirmation;
  }

  pendingDiscardConfirmation = new Promise<boolean>((resolve) => {
    utils.getConfirmation(
      translate.instant('auth.signUp.discardProgressTitle'),
      translate.instant('auth.signUp.discardProgressMessage'),
      (confirmed) => {
        if (confirmed) {
          discardConfirmed = true;
          signUpService.reset();
          resolve(true);
          return;
        }
        resolve(false);
      }
    );
  }).finally(() => {
    pendingDiscardConfirmation = null;
  });

  return pendingDiscardConfirmation;
};
